import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Trash2 } from 'lucide-react';
import { VCardData, VCardPhoneNumber, VCardEmail, VCardWebsite } from '@/lib/vcard-utils';
import { PhoneInput } from '../PhoneInput';

interface VCardContactDetailsProps {
  data: VCardData;
  onChange: (data: Partial<VCardData>) => void;
}

const phoneTypes = [
  { value: 'mobile', label: 'Mobile' },
  { value: 'work', label: 'Work' },
  { value: 'home', label: 'Home' },
  { value: 'fax', label: 'Fax' },
  { value: 'other', label: 'Other' },
];

const emailTypes = [
  { value: 'work', label: 'Work' },
  { value: 'personal', label: 'Personal' },
  { value: 'other', label: 'Other' },
];

const websiteTypes = [
  { value: 'website', label: 'Website' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'portfolio', label: 'Portfolio' },
  { value: 'other', label: 'Other' },
];

export function VCardContactDetails({ data, onChange }: VCardContactDetailsProps) {
  // Phone number handlers
  const addPhone = () => {
    if (data.phoneNumbers.length >= 5) return;
    const newPhone: VCardPhoneNumber = {
      id: Date.now().toString(),
      type: 'work',
      countryCode: 'US',
      number: '',
    };
    onChange({ phoneNumbers: [...data.phoneNumbers, newPhone] });
  };

  const updatePhone = (id: string, updates: Partial<VCardPhoneNumber>) => {
    onChange({
      phoneNumbers: data.phoneNumbers.map((p) =>
        p.id === id ? { ...p, ...updates } : p
      ),
    });
  };

  const removePhone = (id: string) => {
    if (data.phoneNumbers.length <= 1) return;
    onChange({ phoneNumbers: data.phoneNumbers.filter((p) => p.id !== id) });
  };

  // Email handlers
  const addEmail = () => {
    if (data.emails.length >= 5) return;
    const newEmail: VCardEmail = {
      id: Date.now().toString(),
      type: 'work',
      email: '',
    };
    onChange({ emails: [...data.emails, newEmail] });
  };

  const updateEmail = (id: string, updates: Partial<VCardEmail>) => {
    onChange({
      emails: data.emails.map((e) =>
        e.id === id ? { ...e, ...updates } : e
      ),
    });
  };

  const removeEmail = (id: string) => {
    if (data.emails.length <= 1) return;
    onChange({ emails: data.emails.filter((e) => e.id !== id) });
  };

  // Website handlers
  const addWebsite = () => {
    if (data.websites.length >= 5) return;
    const newWebsite: VCardWebsite = {
      id: Date.now().toString(),
      type: 'website',
      url: '',
    };
    onChange({ websites: [...data.websites, newWebsite] });
  };

  const updateWebsite = (id: string, updates: Partial<VCardWebsite>) => {
    onChange({
      websites: data.websites.map((w) =>
        w.id === id ? { ...w, ...updates } : w
      ),
    });
  };

  const removeWebsite = (id: string) => {
    onChange({ websites: data.websites.filter((w) => w.id !== id) });
  };

  return (
    <div className="space-y-8">
      {/* Phone Numbers */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label className="text-base font-medium">Phone Numbers</Label>
          <span className="text-xs text-muted-foreground">{data.phoneNumbers.length}/5</span>
        </div>

        {data.phoneNumbers.map((phone, index) => (
          <div key={phone.id} className="flex items-start gap-2 p-3 bg-muted/30 rounded-lg">
            <div className="flex-1 space-y-3">
              <div className="flex items-center gap-2">
                <Select
                  value={phone.type}
                  onValueChange={(value) => updatePhone(phone.id, { type: value as VCardPhoneNumber['type'] })}
                >
                  <SelectTrigger className="w-[100px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {phoneTypes.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-xs text-muted-foreground">#{index + 1}</span>
              </div>
              <PhoneInput
                value={phone.number}
                onChange={(value) => updatePhone(phone.id, { number: value })}
                countryCode={phone.countryCode}
                onCountryChange={(code) => updatePhone(phone.id, { countryCode: code })}
                showValidation={phone.number.length > 0}
              />
            </div>
            {data.phoneNumbers.length > 1 && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removePhone(phone.id)}
                className="shrink-0 mt-1"
              >
                <Trash2 className="h-4 w-4 text-muted-foreground" />
              </Button>
            )}
          </div>
        ))}

        {data.phoneNumbers.length < 5 && (
          <Button type="button" variant="outline" size="sm" onClick={addPhone} className="w-full">
            <Plus className="h-4 w-4 mr-2" />
            Add Another Phone
          </Button>
        )}
      </div>

      {/* Email Addresses */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label className="text-base font-medium">Email Addresses</Label>
          <span className="text-xs text-muted-foreground">{data.emails.length}/5</span>
        </div>

        {data.emails.map((email, index) => (
          <div key={email.id} className="flex items-center gap-2">
            <Select
              value={email.type}
              onValueChange={(value) => updateEmail(email.id, { type: value as VCardEmail['type'] })}
            >
              <SelectTrigger className="w-[100px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {emailTypes.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="email"
              value={email.email}
              onChange={(e) => updateEmail(email.id, { email: e.target.value })}
              placeholder="email@example.com"
              className="flex-1"
            />
            {data.emails.length > 1 && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeEmail(email.id)}
              >
                <Trash2 className="h-4 w-4 text-muted-foreground" />
              </Button>
            )}
          </div>
        ))}

        {data.emails.length < 5 && (
          <Button type="button" variant="outline" size="sm" onClick={addEmail} className="w-full">
            <Plus className="h-4 w-4 mr-2" />
            Add Another Email
          </Button>
        )}
      </div>

      {/* Websites */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label className="text-base font-medium">Websites</Label>
          <span className="text-xs text-muted-foreground">{data.websites.length}/5</span>
        </div>

        {data.websites.map((website) => (
          <div key={website.id} className="flex items-center gap-2">
            <Select
              value={website.type}
              onValueChange={(value) => updateWebsite(website.id, { type: value as VCardWebsite['type'] })}
            >
              <SelectTrigger className="w-[100px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {websiteTypes.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="url"
              value={website.url}
              onChange={(e) => updateWebsite(website.id, { url: e.target.value })}
              placeholder="https://example.com"
              className="flex-1"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => removeWebsite(website.id)}
            >
              <Trash2 className="h-4 w-4 text-muted-foreground" />
            </Button>
          </div>
        ))}

        {data.websites.length < 5 && (
          <Button type="button" variant="outline" size="sm" onClick={addWebsite} className="w-full">
            <Plus className="h-4 w-4 mr-2" />
            Add Website
          </Button>
        )}
      </div>
    </div>
  );
}
